import { useScrollReveal } from "@/hooks/useScrollReveal";

export interface WhyFeature {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsProps {
  title?: string;
  description?: string;
  features?: WhyFeature[];
  imageUrl?: string;
}

const defaultFeatures: WhyFeature[] = [
  {
    icon: "ri-user-heart-line",
    title: "Genuinely Customer-First",
    description:
      "Our team — Ahmed, Muhammad, and Chris — are known for calling after hours to make sure you made it home safely. That is the level of care we deliver.",
  },
  {
    icon: "ri-shield-check-line",
    title: "15-Day Warranty on Every Vehicle",
    description:
      "Every vehicle comes with a 15-day warranty included at no extra cost. We stand behind every car we sell with full confidence.",
  },
  {
    icon: "ri-bank-card-line",
    title: "Finance Solutions for Everyone",
    description:
      "Good credit, bad credit, first-time buyer — our finance manager Muhammad works with multiple lenders to get you approved and driving.",
  },
  {
    icon: "ri-trophy-line",
    title: "Transparent, Honest Pricing",
    description:
      "No hidden fees, no high-pressure tactics. We believe in upfront, honest pricing that respects your time and your budget.",
  },
];

const DEFAULT_IMAGE =
  "https://public.readdy.ai/ai/img_res/15fb3fbbf9c17b5f593622c273568a0e.jpg";

export default function WhyChooseUs({
  title = "Why Farmington Families Choose United Auto Group",
  description = "We are not just another used car lot. We are a team of automotive professionals who genuinely care about every customer who walks through our door. Our reputation is built on honest dealings, high-quality inventory, and a level of personal service that larger dealerships simply cannot match.",
  features = defaultFeatures,
  imageUrl = DEFAULT_IMAGE,
}: WhyChooseUsProps) {
  const { ref: sectionRef, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-white py-20 md:py-28 overflow-hidden"
      aria-labelledby="why-heading"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={sectionRef}
          className="flex flex-col lg:flex-row items-stretch gap-0"
        >
          {/* Left — Image with overlapping stat cards */}
          <div
            className={`lg:w-5/12 relative transition-all duration-800 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="relative w-full h-[420px] md:h-[560px] rounded-2xl overflow-hidden">
              <img
                src={imageUrl}
                alt="Premium vehicle at United Auto Group Farmington MN"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
            </div>

            {/* Floating stat — bottom left */}
            <div className="absolute bottom-6 left-6 bg-charcoal/92 backdrop-blur-sm rounded-xl px-5 py-4 border border-gold-DEFAULT/20">
              <div className="text-gold-DEFAULT font-serif text-3xl font-semibold">5.0★</div>
              <div className="text-white text-xs font-sans mt-0.5 whitespace-nowrap">
                47+ Google Reviews
              </div>
            </div>

            {/* Floating stat — top right, overlapping */}
            <div className="absolute top-6 right-6 bg-gold-DEFAULT rounded-xl px-5 py-4 text-center">
              <div className="text-charcoal font-serif text-3xl font-bold leading-none">15</div>
              <div className="text-charcoal/70 text-xs font-sans font-semibold mt-0.5 whitespace-nowrap">Day Warranty</div>
            </div>

            {/* Pull-out stripe */}
            <div className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-24 bg-cream-DEFAULT rounded-r-2xl" />
          </div>

          {/* Right — Content, overlaps slightly on image */}
          <div
            className={`lg:w-7/12 lg:-ml-4 bg-cream-DEFAULT rounded-2xl py-12 px-8 md:px-12 flex flex-col justify-center transition-all duration-800 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
            style={{ transitionDelay: "150ms" }}
          >
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                WHY CHOOSE US
              </span>
            </div>
            <h2
              id="why-heading"
              className="font-serif text-charcoal leading-tight mb-5"
              style={{ fontSize: "clamp(1.8rem, 3vw, 2.7rem)", lineHeight: 1.1 }}
            >
              {title}
            </h2>
            <p className="text-charcoal-soft font-sans text-base leading-relaxed mb-7 max-w-lg">
              {description}
            </p>

            {/* Real customer pull-quote */}
            <blockquote className="border-l-3 border-l-[3px] border-gold-DEFAULT pl-5 py-1 mb-8 bg-gold-DEFAULT/5 rounded-r-xl pr-4">
              <p className="font-serif text-charcoal italic text-base leading-relaxed">
                &ldquo;It was so refreshing to go to a dealer where they ask you questions because they actually care about your needs.&rdquo;
              </p>
              <footer className="mt-2 text-xs text-charcoal-soft font-sans font-semibold flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-2.5 h-2.5 flex items-center justify-center">
                      <i className="ri-star-fill text-gold-DEFAULT text-[10px]" />
                    </div>
                  ))}
                </div>
                — Aaron Rousu, Verified Google Review
              </footer>
            </blockquote>

            {/* Features */}
            <div className="flex flex-col gap-5">
              {features.map((feat, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gold-DEFAULT/15 flex-shrink-0">
                    <i className={`${feat.icon} text-gold-DEFAULT text-base`} />
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-charcoal text-sm mb-1 tracking-wide">
                      {feat.title}
                    </h3>
                    <p className="text-charcoal-soft text-sm leading-relaxed font-sans">
                      {feat.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href="https://www.uagmn.com/inventory/"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-charcoal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                View Inventory
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </a>
              <a
                href="tel:9527693208"
                className="inline-flex items-center justify-center gap-2 border-2 border-charcoal/30 text-charcoal font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm" />
                </div>
                (952) 769-3208
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
